import { Fragment, useEffect, useId, useState, type ElementType } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MENU_NAV_STATE } from '../hooks/backNavigationModel';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FileText, Home, LayoutGrid, MessageSquare, Settings } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { useActiveNavPath, useNavGroups, type NavItem } from './navigation';
import Dialog from './ui/Dialog';
import { AccountRadioRow, Avatar, useAccountItems } from './ui/AvatarMenu';
import type { MenuItemDef } from './ui/Menu';
import type { NavGroupId } from './navModel';
import ChatCountBadge from './ChatCountBadge';

/**
 * Mobile tab bar + "עוד" sheet (T-32, PRD §4.4, controls inventory §5.2).
 *
 * Tab bar (< md, `mat-chrome`, 64 px + safe area): היום · קריאות · שיחות · עוד.
 * Active = accent text + an accent-soft pill behind the icon + weight 500.
 * QuickCreate is the floating "+" above the bar (owner, 2026-10-06; supersedes
 * Q-020b's middle slot), so the bar holds navigation only.
 * "עוד" opens a large sheet that mirrors the sidebar groups (same model, so
 * nothing is search-only on a phone), then the account rows that the avatar
 * menu holds on desktop.
 */

/** `key` is also the nav id: a tab shows only when its screen is open (`usePermissions`). */
const TABS: { to: string; key: string; icon: ElementType }[] = [
  { to: '/', key: 'today', icon: Home },
  { to: '/tickets', key: 'tickets', icon: FileText },
  { to: '/discussions', key: 'conversations', icon: MessageSquare },
];

function SheetRow({ item, group, active }: { item: NavItem; group: NavGroupId; active: boolean }) {
  const Icon = item.icon;
  return (
    <li>
      <Link to={item.to} state={MENU_NAV_STATE} aria-current={active ? 'page' : undefined} data-nav-group={group} className="nav-row min-h-touch px-3">
        <Icon size={20} strokeWidth={1.75} className="nav-row-icon flex-shrink-0" aria-hidden="true" />
        <span className="truncate text-base">{item.label}</span>
      </Link>
    </li>
  );
}

function AccountRow({ item, onPick }: { item: MenuItemDef; onPick: (item: MenuItemDef) => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(item)}
        lang={item.lang}
        aria-label={item.ariaLabel}
        className={`nav-row min-h-touch w-full px-3 ${item.danger ? 'text-danger-700' : ''}`}
      >
        <span className="nav-row-icon flex size-5 flex-shrink-0 items-center justify-center text-text-3" aria-hidden="true">{item.icon}</span>
        <span className="flex-1 truncate text-start text-base">{item.label}</span>
        {item.trailing && <span className="flex flex-shrink-0 items-center gap-1.5 text-sm text-text-2">{item.trailing}</span>}
      </button>
    </li>
  );
}

function MoreSheet({ open, onClose, onLogout }: { open: boolean; onClose: () => void; onLogout: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, organization } = useAuthStore();
  const groups = useNavGroups();
  const active = useActiveNavPath(groups, location.pathname);
  const accountItems = useAccountItems(onLogout);
  const pinned = groups.find((g) => g.pinned);
  const pinnedHasActive = !!pinned?.items.some((i) => i.to === active);
  // C-35 (clarity, D-21): the role that opens the settings page gets one "הגדרות" row (as in the sidebar).
  const settingsRow = !!pinned?.items.some((i) => i.id === 'systemSettings');
  const [adminOpen, setAdminOpen] = useState(pinnedHasActive);
  const adminListId = useId();
  useEffect(() => { if (open) setAdminOpen(pinnedHasActive); }, [open, pinnedHasActive]);

  const pick = (item: MenuItemDef) => {
    onClose();
    // After the sheet starts leaving (and focus went back), like the desktop menu.
    requestAnimationFrame(() => item.onSelect?.());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('shell.mobile.moreTitle')}
      variant="sheet"
      detent="large"
      bodyClassName="px-3 pb-4"
    >
      <nav aria-label={t('aria.mainNav')}>
        {groups.filter((g) => !g.pinned).map((group) => {
          // C-29 (clarity, A-28): no "לקוחות" heading right above a "לקוחות" row; a group name that
          // equals its first item's label is left out. Not a heading (B-21), the group is labelled.
          const headingId = `more-group-${group.id}`;
          const showLabel = !!group.label && group.label !== group.items[0]?.label;
          return (
            <Fragment key={group.id}>
              {showLabel && <p id={headingId} role="presentation" className="px-3 pb-1 pt-4 text-xs font-medium text-text-3">{group.label}</p>}
              <ul className="flex flex-col gap-0.5" aria-labelledby={showLabel ? headingId : undefined} aria-label={!showLabel && group.label ? group.label : undefined}>
                {group.items.map((item) => <SheetRow key={item.to} item={item} group={group.id} active={item.to === active} />)}
              </ul>
            </Fragment>
          );
        })}
        {pinned && settingsRow && (
          <div className="mt-3 border-t border-border pt-2">
            <ul className="flex flex-col gap-0.5">
              <li>
                <Link to="/settings/system" state={MENU_NAV_STATE} aria-current={pinnedHasActive ? 'page' : undefined} data-nav-group="admin" data-nav-settings="" className="nav-row min-h-touch px-3">
                  <Settings size={20} strokeWidth={1.75} className="nav-row-icon flex-shrink-0" aria-hidden="true" />
                  <span className="truncate text-base">{t('shell.navGroup.settings')}</span>
                </Link>
              </li>
            </ul>
          </div>
        )}
        {pinned && !settingsRow && (
          <div className="mt-3 border-t border-border pt-2">
            <button
              type="button"
              onClick={() => setAdminOpen((o) => !o)}
              aria-expanded={adminOpen}
              aria-controls={adminListId}
              data-nav-group="admin"
              className="nav-row min-h-touch w-full px-3"
            >
              <Settings size={20} strokeWidth={1.75} className="nav-row-icon flex-shrink-0" aria-hidden="true" />
              <span className="flex-1 truncate text-start text-base">{pinned.label}</span>
              <ChevronDown size={18} strokeWidth={1.75} className={`text-text-3 transition-transform duration-standard ${adminOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            <ul id={adminListId} className={`flex-col gap-0.5 ${adminOpen ? 'flex' : 'hidden'}`}>
              {pinned.items.map((item) => <SheetRow key={item.to} item={item} group={pinned.id} active={item.to === active} />)}
            </ul>
          </div>
        )}
      </nav>

      <div className="mt-3 border-t border-border pt-3">
        <div className="flex items-center gap-3 px-3 pb-2">
          <Avatar name={user?.name} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-text">{user?.name}</p>
            <p className="truncate text-xs text-text-2">{user?.email ? <bdi dir="ltr">{user.email}</bdi> : organization?.name}</p>
          </div>
        </div>
        <ul className="flex flex-col gap-0.5" aria-label={t('shell.userMenu.account')}>
          {accountItems.map((item, i) => item.radioGroup
            ? accountItems[i - 1]?.radioGroup?.id === item.radioGroup.id ? null : <AccountRadioRow key={item.key} items={accountItems.filter((x) => x.radioGroup?.id === item.radioGroup?.id)} />
            : <AccountRow key={item.key} item={item} onPick={pick} />)}
        </ul>
      </div>
    </Dialog>
  );
}

export default function MobileNav({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const { canScreen } = usePermissions();
  const [moreOpen, setMoreOpen] = useState(false);
  const tabs = TABS.filter((tb) => canScreen(tb.key));

  // Navigating from the sheet closes it.
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  if (!user) return null;

  const isTab = (to: string) => (to === '/' ? location.pathname === '/' : location.pathname === to || location.pathname.startsWith(`${to}/`));
  const moreActive = moreOpen || !tabs.some((tb) => isTab(tb.to));

  const tab = (item: { to: string; key: string; icon: ElementType }) => {
    const Icon = item.icon;
    const active = isTab(item.to);
    return (
      <Link
        key={item.to}
        to={item.to}
        state={MENU_NAV_STATE}
        aria-current={active ? 'page' : undefined}
        className={`flex h-full min-w-0 flex-1 flex-col items-center justify-center gap-0.5 transition-colors duration-micro ${
          active ? 'text-accent-text font-medium' : 'text-text-2'
        }`}
      >
        <span className="tabbar-pill relative">
          <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
          {item.to === '/discussions' && <ChatCountBadge className="absolute -top-1 -end-2" />}
        </span>
        <span className="max-w-full truncate text-xs leading-none">{t(`shell.mobile.${item.key}`)}</span>
      </Link>
    );
  };

  return (
    <>
      <nav
        data-overlay-inert=""
        className="mat-chrome fixed inset-x-0 bottom-0 z-nav border-t border-border md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label={t('aria.mainNav')}
      >
        <div className="flex h-16 items-stretch justify-around px-1">
          {tabs.map(tab)}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            data-active={moreActive ? '' : undefined}
            className={`flex h-full min-w-0 flex-1 flex-col items-center justify-center gap-0.5 transition-colors duration-micro ${
              moreActive ? 'text-accent-text font-medium' : 'text-text-2'
            }`}
          >
            <span className="tabbar-pill">
              <LayoutGrid size={20} strokeWidth={1.75} aria-hidden="true" />
            </span>
            <span className="text-xs leading-none">{t('shell.mobile.more')}</span>
          </button>
        </div>
      </nav>

      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} onLogout={onLogout} />
    </>
  );
}
